import React from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, LogOut } from 'lucide-react';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'warning' | 'danger' | 'info';
  icon?: React.ReactNode;
  isLoading?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'warning',
  icon,
  isLoading = false
}) => {
  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          iconBg: 'bg-red-50',
          iconColor: 'text-red-600',
          confirmBtn: 'bg-red-600 hover:bg-red-700 text-white transition-colors',
        };
      case 'warning':
        return {
          iconBg: 'bg-gray-50',
          iconColor: 'text-gray-600',
          confirmBtn: 'bg-gray-600 hover:bg-gray-700 text-white transition-colors',
        };
      case 'info':
        return {
          iconBg: 'bg-gray-50',
          iconColor: 'text-gray-600',
          confirmBtn: 'bg-gray-600 hover:bg-gray-700 text-white transition-colors',
        };
      default:
        return {
          iconBg: 'bg-gray-50',
          iconColor: 'text-gray-600',
          confirmBtn: 'bg-black hover:bg-gray-800 text-white transition-colors',
        };
    }
  };

  const styles = getTypeStyles();
  const defaultIcon = type === 'danger' ? <LogOut size={24} /> : <AlertTriangle size={24} />;

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] p-4">
      <div className="bg-white rounded-3xl max-w-md w-full p-8 animate-fade-up shadow-2xl border border-gray-100">
        <div className="flex items-start space-x-4">
          {/* Icon */}
          <div className={`p-4 rounded-full ${styles.iconBg} flex-shrink-0 ring-1 ring-white`}>
            <div className={styles.iconColor}>
              {icon || defaultIcon}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              {title}
            </h3>
            <p className="text-sm text-gray-600 mb-8 leading-relaxed">
              {message}
            </p>

            {/* Actions */}
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 px-6 py-3 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {cancelText}
              </button>
              <button
                onClick={async () => {
                  await onConfirm();
                }}
                disabled={isLoading}
                className={`flex-1 px-6 py-3 text-sm font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ${styles.confirmBtn}`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Loading...</span>
                  </div>
                ) : (
                  confirmText
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render modal at document root level to avoid z-index stacking issues
  return createPortal(modalContent, document.body);
};

export default ConfirmationModal; 